import { api } from '../config/api';

export interface LoginResponse {
  access_token?: string;
  refresh_token?: string;
  user?: {
    id: string;
    email: string;
    nom: string;
    role: string;
    tenant: any;
    totp_enabled?: boolean;
  };
  last_login_at?: string | null;
  failed_attempts?: number;
  requires_totp?: boolean;
  requires_totp_setup?: boolean;
  totp_session_token?: string;
}

export const authService = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  /** Crée le premier admin_entreprise d'un tenant tout juste créé via registerTenant() */
  register: async (userData: {
    nom: string;
    email: string;
    telephone: string;
    password: string;
    tenant_id: string;
  }) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  /** Onboarding self-service : crée une nouvelle entreprise (tenant) */
  registerTenant: async (tenantData: { nom: string; type: string }) => {
    const response = await api.post('/auth/register-tenant', tenantData);
    return response.data;
  },

  /** Répertoire public des entreprises actives, pour le sélecteur d'inscription particulier */
  getCompanies: async (): Promise<{ id: string; nom: string }[]> => {
    const response = await api.get('/auth/companies');
    return response.data;
  },

  /** Super Admin uniquement : crée une entreprise ET son premier admin en une seule opération */
  createTenantWithAdmin: async (data: {
    nom: string;
    type: string;
    email?: string;
    telephone?: string;
    devise?: string;
    ville?: string;
    pays?: string;
    // 2FA (TOTP) obligatoire pour l'admin de ce tenant — actif par défaut
    // côté backend si omis (spéc. 28/09/26).
    totp_required?: boolean;
    admin_nom: string;
    admin_email: string;
    admin_telephone: string;
    admin_password: string;
  }) => {
    const response = await api.post('/auth/create-tenant-with-admin', data);
    return response.data;
  },

  // ── Module Expéditeur — inscription / connexion sans mot de passe ─────────
  // (remplace l'ancienne auto-inscription particulier par e-mail + mot de passe)

  /** Inscription express Particulier / Vendeur Social (téléphone + OTP) */
  registerExpediteur: async (data: {
    nom: string;
    telephone: string;
    tenant_id: string;
    profil_type: 'particulier' | 'vendeur_social';
    email?: string;
    boutique_nom?: string;
    ramassage_ville?: string;
    ramassage_quartier?: string;
    cod_reversement_moyen?: 'om' | 'momo' | 'cash';
  }): Promise<{ message: string }> => {
    const response = await api.post('/auth/expediteur/register', data);
    return response.data;
  },

  /** Connexion expéditeur — demande d'un code OTP au numéro d'un compte existant */
  requestExpediteurOtp: async (telephone: string): Promise<{ sent: boolean }> => {
    const response = await api.post('/auth/expediteur/otp', { telephone });
    return response.data;
  },

  /** Vérifie le code OTP téléphone et connecte l'expéditeur */
  verifyExpediteurOtp: async (telephone: string, code: string): Promise<LoginResponse> => {
    const response = await api.post('/auth/expediteur/verify', { telephone, code });
    return response.data;
  },

  /** Révoque le refresh token côté serveur (best-effort) */
  logout: async (): Promise<void> => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Le token local est supprimé de toute façon par le store
    }
  },

  // ── TOTP ────────────────────────────────────────────────────────────────

  /** Step 1: request QR code for forced setup */
  setupForcedTotp: async (totp_session_token: string): Promise<{ secret: string; qr_data_url: string; otpauth_url: string }> => {
    const response = await api.post('/auth/totp/setup-forced', { totp_session_token });
    return response.data;
  },

  /** Step 2: verify first TOTP code → returns full JWT */
  verifySetupForcedTotp: async (totp_session_token: string, code: string): Promise<LoginResponse> => {
    const response = await api.post('/auth/totp/verify-setup-forced', { totp_session_token, code });
    return response.data;
  },

  /** Validate TOTP code at login → returns full JWT */
  validateTotpLogin: async (totp_session_token: string, code: string): Promise<LoginResponse> => {
    const response = await api.post('/auth/totp/validate', { totp_session_token, code });
    return response.data;
  },

  /** Get current user info (refreshes totp_enabled etc.) */
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  /** SUPER_ADMIN only: reset a user's TOTP */
  resetUserTotp: async (userId: string): Promise<{ success: boolean }> => {
    const response = await api.delete(`/auth/totp/${userId}`);
    return response.data;
  },
};
